import React, { useRef, useState } from 'react';
import { AlertTriangle, FileSpreadsheet, X } from 'lucide-react';
import {
  ACCEPTED_EXTENSIONS, DATE_SOURCE_LABELS, GROUPS, formatNumber, formatRange, money, periodDays,
} from './sncShared';

/**
 * Everything above the name rows (spec §12 "Worklist"): the file line (one
 * line per file, remove ×, a second file can be dropped here at any time),
 * Ignored rows (n), the short-period warning, the coverage bar in % and ₹,
 * the four tabs with counts, the search box and the progress line.
 * `children` render inside the toolbar next to the search box.
 */
export default function WorklistHeader({
  uploads, ignoredRows, coverage, uploading, uploadError, busy,
  onAddFile, onRemoveUpload, groupCounts, activeGroup, onGroup,
  search, onSearch, undecided, conflictCount, children,
}) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [showIgnored, setShowIgnored] = useState(false);

  const files = uploads || [];
  const ignored = ignoredRows || [];
  const multi = files.length > 1;
  const days = periodDays(files);

  const total = Number((coverage && coverage.totalAmount) || 0);
  const covered = Number((coverage && coverage.coveredAmount) || 0);
  const skipped = Number((coverage && coverage.skippedAmount) || 0);
  const coveredPct = total > 0 ? Math.round((covered / total) * 100) : 0;
  const skippedPct = total > 0 ? Math.min(100 - coveredPct, Math.round((skipped / total) * 100)) : 0;

  return (
    <>
      <div
        className={`snc-files${dragging ? ' snc-files--drop' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (!uploading && !busy) onAddFile(e.dataTransfer?.files?.[0]);
        }}
      >
        {files.map((u) => (
          <div key={u.uploadId} className="snc-fileline">
            <div className="snc-fileline__left">
              <FileSpreadsheet size={16} className="snc-fileline__icon" />
              <span className="snc-fileline__text" title={u.fileName}>
                <strong>{u.fileName}</strong>
                {' · '}{formatRange(u.dateFrom, u.dateTo)}
                {DATE_SOURCE_LABELS[u.dateSource] ? ` (${DATE_SOURCE_LABELS[u.dateSource]})` : ''}
                {' · '}{formatNumber(u.nameCount)} names
              </span>
              <button
                type="button"
                className="snc-icon-btn"
                onClick={() => onRemoveUpload(u)}
                disabled={busy || uploading}
                aria-label={`Remove ${u.fileName}`}
                title="Remove file"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        ))}
        <div className="snc-files__actions">
          <button
            type="button"
            className="snc-link-btn"
            onClick={() => inputRef.current && inputRef.current.click()}
            disabled={busy || uploading}
          >
            {uploading ? 'Reading file…' : 'Add another file'}
          </button>
          <button
            type="button"
            className="snc-link-btn"
            onClick={() => setShowIgnored((v) => !v)}
            aria-expanded={showIgnored}
          >
            Ignored rows: {ignored.length}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_EXTENSIONS.join(',')}
            className="snc-file-input"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              onAddFile(file);
            }}
          />
        </div>
        {uploadError && <p className="snc-error" role="alert">{uploadError}</p>}
      </div>

      {showIgnored && (
        <div className="snc-ignored">
          {ignored.length === 0 && <p className="snc-ignored__empty">No rows were ignored.</p>}
          {ignored.map((r, i) => (
            <div key={`${r.uploadId}-${r.row}-${i}`} className="snc-ignored__row">
              <span className="snc-ignored__num">Row {r.row}</span>
              <span className="snc-ignored__text">{multi ? `${r.fileName}: ` : ''}{r.text || '(blank)'}</span>
              <span className="snc-tag">{r.reason}</span>
            </div>
          ))}
        </div>
      )}

      {days !== null && days < 25 && (
        <div className="snc-banner" role="status">
          <AlertTriangle size={15} className="snc-banner__icon" />
          <span>
            {multi ? 'These files cover' : 'This file covers'} {days} day{days === 1 ? '' : 's'}.
            {' '}Dishes sold only on weekends or rarely may be missed.
          </span>
        </div>
      )}

      <div className="snc-coverage">
        <div className="snc-coverage__bar" aria-label={`Revenue covered ${coveredPct}%`}>
          <div className="snc-coverage__fill" style={{ width: `${coveredPct}%` }} />
          <div className="snc-coverage__skipped" style={{ width: `${skippedPct}%` }} />
        </div>
        <p className="snc-coverage__label">
          Revenue covered: {coveredPct}% ({money(covered)}) · Skipped: {skippedPct}% ({money(skipped)}) · Total {money(total)}
        </p>
      </div>

      <div className="snc-tabs" role="tablist">
        {GROUPS.map((g) => (
          <button
            key={g.key}
            type="button"
            role="tab"
            aria-selected={activeGroup === g.key}
            className={`snc-tab${activeGroup === g.key ? ' snc-tab--active' : ''}`}
            onClick={() => onGroup(g.key)}
          >
            {g.label} {groupCounts[g.key] || 0}
          </button>
        ))}
      </div>

      <div className="snc-toolbar">
        <input
          type="search"
          className="snc-search"
          placeholder="Search names…"
          aria-label="Search names"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
        />
        {children}
      </div>

      <p className="snc-progress">
        {undecided.count === 0
          ? 'Every name has a decision'
          : `${formatNumber(undecided.count)} name${undecided.count === 1 ? '' : 's'} left · ${money(undecided.amount)}`}
        {conflictCount > 0 ? ` · ${conflictCount} conflict${conflictCount === 1 ? '' : 's'} to resolve` : ''}
      </p>
    </>
  );
}
